import { IBreeamStat } from "@mctypes/global.types";
import { IIssueBoardApi } from "@mcapps/shared";
import { AgGridReact } from "ag-grid-react";
import { Box, Card, Flex, Tooltip } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useLocalStorage } from "usehooks-ts";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { Checkbox } from "../../../../../components";
import { addAppModalThunk } from "../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { AppModalEnums } from "@mcapps/shared/sharedConstants";
import { BREEAM_DASHBOARD_PHASES, ISSUE_COMPLETED_VALUE } from "../../issueConstants";
import { getIssueUrlPath } from "../../issueUtils";
import BreeamBarChart, { BreeamStackedBarChart } from "./BreeamBarChart";
import GaugeChartSVG from "./GaugeChartSVG";
import { selectFilteredIssuesOnBoard } from "../../issueSelectors";
import McDropdownNew from "../../../../../components/McDropdown/MCDropdownNew";

export const breeamAttributeTitles = [
  {
    attribute: "maxPoints",
    key: "D",
    titleAlternatives: ["Mulige poeng", "Antall oppnåelige BREEAM poeng", "Antall oppnåelige poeng"],
  },
  {
    attribute: "plannedPoints",
    key: "E",
    titleAlternatives: ["Planlagte poeng", "Antatt oppnådde poeng", "Antatt oppnåelige poeng"],
  },
  { attribute: "achievedPoints", key: "F", titleAlternatives: ["Oppnådde poeng", "Faktisk oppnådde poeng"] },
];
export enum OtherAttributeEnum {
  MaxPoints = "D",
  PlannedPoints = "E",
  AchievedPoints = "F",
  DifficultyLevel = "B",
}

export enum BreeamDataEnum {
  BucketOptionOmitted = "Kriteriet er utelatt",
}
const BreeamDashboard: React.FC<{ projectId: string; issueBoard: IIssueBoardApi }> = ({ projectId, issueBoard }) => {
  const filteredIssues = useAppSelector(selectFilteredIssuesOnBoard);

  const getBreeamValueCount: IBreeamStat = React.useMemo(() => {
    let primaryTotal = 0;
    let primaryAltTotal = 0;
    let secondaryTotal = 0;
    let maxTotal = 0;
    let optionalTotal = 0;
    filteredIssues.forEach((item) => {
      const otherAttributesData = item.otherAttributesData || {};
      const primaryValue =
        item.status === ISSUE_COMPLETED_VALUE ? Number(otherAttributesData[OtherAttributeEnum.PlannedPoints]) || 0 : 0;
      const primaryValueAlt = Number(otherAttributesData[OtherAttributeEnum.AchievedPoints]) || 0; //some projects use achieved points instead of 'documentation received'
      const secondaryValue = Number(otherAttributesData[OtherAttributeEnum.PlannedPoints]) || 0; //Antatt Oppnådde poeng
      const maxValue =
        item.bucket && item.bucket.replace(/\s+/g, "").toLowerCase() === "kriterieterutelatt"
          ? 0
          : Number(otherAttributesData[OtherAttributeEnum.MaxPoints]) || 0; //Mulige poeng
      const optionalValue = item.bucket.toLowerCase().includes("usikker")
        ? Number(otherAttributesData[OtherAttributeEnum.PlannedPoints] || 0) //Antatt oppnådde poeng
        : 0;
      // Update totals
      primaryTotal += primaryValue;
      primaryAltTotal += primaryValueAlt;
      secondaryTotal += secondaryValue;
      maxTotal += maxValue;
      optionalTotal += optionalValue;
    });
    // Calculate valueArray and maxTotal with clear logic, avoiding nested ternaries
    const mainValue = primaryTotal > primaryAltTotal ? primaryTotal : primaryAltTotal;
    const maxValue = Math.max(secondaryTotal, maxTotal, 1); // Ensure at least 1

    return {
      valueArray: [mainValue, secondaryTotal, secondaryTotal - optionalTotal],
      maxTotal: maxValue,
    };
  }, [filteredIssues]);

  const getPreanalyseValueCount: IBreeamStat = React.useMemo(() => {
    let total_1 = 0;
    let total_2 = 0;
    let total_3 = 0;
    let total_4 = 0;
    let maxTotal = 0;
    filteredIssues.forEach((item) => {
      const normalizedDifficultyKey =
        typeof item?.otherAttributesData?.[OtherAttributeEnum.DifficultyLevel] === "string"
          ? item.otherAttributesData[OtherAttributeEnum.DifficultyLevel].replace(/\s+/g, "").toLowerCase()
          : "";
      const maxValue = Number(item?.otherAttributesData?.[OtherAttributeEnum.MaxPoints]) || 0;
      const plannedValue = Number(item?.otherAttributesData?.[OtherAttributeEnum.PlannedPoints]) || 0;
      maxTotal += maxValue;

      switch (normalizedDifficultyKey) {
        case "antasoppnås":
          total_1 += plannedValue;
          total_2 += plannedValue;
          total_3 += plannedValue;
          total_4 += plannedValue;
          break;
        case "krevernoe":
          total_2 += plannedValue;
          total_3 += plannedValue;
          total_4 += plannedValue;
          break;
        case "krevermer":
          total_3 += plannedValue;
          total_4 += plannedValue;
          break;
        case "krevermye":
          total_4 += plannedValue;
          break;
        default:
          break;
      }
    });
    return {
      valueArray: [total_1, total_2, total_3, total_4],
      maxTotal: maxTotal || 1,
    };
  }, [filteredIssues]);

  return (
    <Box style={{ height: "100%", width: "100%", position: "relative", overflowX: "auto" }}>
      {filteredIssues != null && (
        <BreeamStatsCard
          breeamStat={getBreeamValueCount}
          preanalyseStat={getPreanalyseValueCount}
          items={filteredIssues}
          issueBoard={issueBoard}
          projectId={projectId}
        />
      )}
    </Box>
  );
};

export default BreeamDashboard;

const BreeamStatsCard: React.FC<{
  breeamStat?: IBreeamStat;
  preanalyseStat?: IBreeamStat;
  items: any[];
  issueBoard: IIssueBoardApi;
  projectId: string;
}> = ({ breeamStat, preanalyseStat, items, issueBoard, projectId }) => {
  const [breemDashboardPhase, setBreeamDashboardPhase] = useState(BREEAM_DASHBOARD_PHASES.ANALYSE);
  const [showOptionalPoints, setShowOptionalPoints] = useLocalStorage("optionalBREEAMDial", false);

  const history = useHistory();
  const dispatch = useAppDispatch();

  const openIssue = (_id: string) => {
    history.push(getIssueUrlPath(history.location.pathname, _id));
    dispatch(
      addAppModalThunk({
        itemId: _id,
        boardId: issueBoard._id,
        projectId: projectId,
        app: AppModalEnums.ISSUE,
      }),
    );
  };

  useEffect(() => {
    if (issueBoard.name.toLowerCase().includes("preanalyse")) {
      setBreeamDashboardPhase(BREEAM_DASHBOARD_PHASES.PREANALYSE);
    } else {
      setBreeamDashboardPhase(BREEAM_DASHBOARD_PHASES.ANALYSE);
    }
  }, [issueBoard.name]);

  const generateCountColumns = (): any[] => {
    return breeamAttributeTitles.map((attribute) => {
      return {
        aggFunc: "sum",
        headerName: issueBoard.otherAttributesConfig?.[attribute.key]?.label,
        field: `otherAttributesData.${attribute.key}`,
        valueGetter: (params: any) => {
          try {
            const value =
              attribute.key === OtherAttributeEnum.MaxPoints && params.data.bucket == "Kriteriet er utelatt"
                ? 0
                : Number(params.data.otherAttributesData[attribute.key]);
            return isNaN(value) ? 0 : value;
          } catch (error) {
            return 0;
          }
        },
      };
    });
  };

  const columnDefs = [
    {
      headerName: "Kategori",
      field: "typeOfIssue",
      rowGroup: true,
      hide: true,
    },
    {
      headerName: "Tittel",
      field: "title",
      rowGroup: true,
      hide: true,
    },
    ...generateCountColumns(),
    {
      aggFunc: "sum",
      headerName: "Utelatte poeng",
      field: `otherAttributesData.{${OtherAttributeEnum.MaxPoints}}`,
      valueGetter: (params: any) => {
        try {
          const value =
            params.data.bucket == BreeamDataEnum.BucketOptionOmitted
              ? Number(params.data.otherAttributesData[OtherAttributeEnum.MaxPoints])
              : 0;
          return isNaN(value) ? 0 : value;
        } catch (error) {
          return 0;
        }
      },
    },
  ];
  return (
    <Card className="gray-200" padding="xs" style={{ display: "flex", marginInline: "0.25rem" }}>
      <div>
        <div style={{ width: "180px", position: "absolute", top: "0.6em", right: "1.5em", zIndex: 1 }}>
          <McDropdownNew
            color="dark"
            variant="filled"
            justify="space-between"
            buttonProps={{ style: { width: "100%" } }}
            currentValue={breemDashboardPhase}
            onChange={(_, newValue: BREEAM_DASHBOARD_PHASES) => {
              setBreeamDashboardPhase(newValue);
            }}
            options={[BREEAM_DASHBOARD_PHASES.ANALYSE, BREEAM_DASHBOARD_PHASES.PREANALYSE]}
          />
        </div>
        <Box style={{ marginTop: "0.75rem" }} />
        <Flex style={{ width: "100%", gap: "0.75rem" }}>
          <Card padding="xs" style={{ width: "50%" }}>
            <div>
              {breemDashboardPhase === BREEAM_DASHBOARD_PHASES.ANALYSE ? (
                <>
                  <GaugeChartSVG
                    valueArray={breeamStat.valueArray}
                    maxValue={breeamStat.maxTotal}
                    valueType="certification"
                  />
                  <div style={{ fontFamily: "helvetica", color: "#636363" }}>
                    <Checkbox
                      text="Vis uavklarte poeng"
                      isChecked={showOptionalPoints}
                      setChecked={() => setShowOptionalPoints(!showOptionalPoints)}
                      disabled={false}
                      size="small"
                    />
                  </div>
                </>
              ) : (
                <GaugeChartSVG
                  valueArray={preanalyseStat.valueArray}
                  maxValue={preanalyseStat.maxTotal}
                  valueType="preanalyse"
                />
              )}
            </div>
          </Card>
          <Card padding="xs" style={{ width: "50%" }}>
            <div>
              {breemDashboardPhase === BREEAM_DASHBOARD_PHASES.ANALYSE ? (
                <BreeamBarChart
                  items={items}
                  dataFields={issueBoard.buckets.map((bucket) => bucket.value)}
                  issueBoard={issueBoard}
                />
              ) : (
                <BreeamStackedBarChart
                  items={items}
                  dataFields={issueBoard.types.map((type) => type.name)}
                  issueBoard={issueBoard}
                />
              )}
            </div>
          </Card>
        </Flex>
        <Box style={{ marginTop: "0.75rem" }} />
        <Flex style={{ width: "100%" }}>
          <Card padding="xs" style={{ width: "100%" }}>
            <div>
              <div className="ag-theme-quartz" style={{ height: "100%" }}>
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    maxWidth: 400,
                    sortable: true,
                    resizable: true,
                    suppressHeaderMenuButton: true,
                    autoHeaderHeight: true,
                    wrapHeaderText: true,
                    cellClass: "d-block",
                  }}
                  cellSelection={true}
                  groupDisplayType={"singleColumn"}
                  localeText={AG_GRID_LOCALE_NOR}
                  columnDefs={columnDefs}
                  suppressAggFuncInHeader={true}
                  rowData={items}
                  domLayout="autoHeight"
                  rowHeight={32}
                  headerHeight={30}
                  autoGroupColumnDef={{
                    flex: 2,
                    headerName: "Kategori",
                    cellRendererParams: {
                      suppressCount: true,
                    },
                    cellRendererSelector: (params) => {
                      if (params.node.allChildrenCount < 2) {
                        return {
                          component: () => {
                            return (
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <Tooltip
                                  withArrow
                                  position="bottom"
                                  multiline
                                  w={220}
                                  label={params.node.allLeafChildren[0]?.data.description}
                                  styles={{
                                    tooltip: {
                                      fontFamily: "system-ui",
                                      textAlign: "left",
                                      minWidth: "150px",
                                    },
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      width: "15px",
                                      height: "15px",
                                      marginRight: "5px",
                                      color: "#ccc",
                                      fontSize: "1.1em",
                                    }}
                                  >
                                    <i
                                      className="fa fa-question-circle fa-fw hoverText"
                                      style={{ cursor: "pointer" }}
                                    />
                                  </div>
                                </Tooltip>

                                <div
                                  className="hoverText"
                                  style={{
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  onClick={() => {
                                    openIssue(params.node.allLeafChildren[0]?.data._id);
                                  }}
                                >
                                  {params.node.key}
                                </div>
                              </div>
                            );
                          },
                        };
                      }
                      return { component: "agGroupCellRenderer" };
                    },
                  }}
                  grandTotalRow={"bottom"}
                />
              </div>
            </div>
          </Card>
        </Flex>
      </div>
    </Card>
  );
};
